import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { apiService } from '../services/api';
import { toast } from 'react-hot-toast';

const Dashboard = () => {
  const { user } = useAuth();
  const [dados, setDados] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const buscar = async () => {
      try {
        const response = await apiService.get('/dashboard');
        if (response.data?.success) {
          setDados(response.data.data);
        }
      } catch (error) {
        console.error('Erro ao carregar dashboard:', error);
        toast.error('Erro ao carregar dados do dashboard');
      } finally {
        setLoading(false);
      }
    };
    buscar();
  }, []);

  const quickActions = [
    { title: 'Registrar Ponto', description: 'Entrada ou saída', icon: '⏰', path: '/ponto', profiles: ['administrador', 'supervisor', 'financeiro', 'motorista', 'monitor', 'auxiliar'] },
    { title: 'Folha de Ponto', description: 'Horas e saldos', icon: '📄', path: '/ponto/folha', profiles: ['administrador', 'supervisor', 'financeiro', 'coordenador', 'administrativo'] },
    { title: 'Histórico de Pontos', description: 'Registros do dia', icon: '📅', path: '/ponto/historico', profiles: ['administrador', 'supervisor', 'financeiro', 'motorista'] },
    { title: 'Gerenciar Frota', description: 'Veículos e rotas', icon: '🚛', path: '/frota', profiles: ['administrador', 'supervisor', 'financeiro', 'coordenador'] },
    { title: 'Colaboradores', description: 'Gestão de funcionários', icon: '👥', path: '/rh/colaboradores', profiles: ['administrador', 'supervisor', 'financeiro', 'coordenador'] },
    { title: 'Atestados', description: 'Homologação médica', icon: '📋', path: '/rh/atestados', profiles: ['administrador', 'supervisor', 'financeiro'] },
    { title: 'Relatórios', description: 'Análises e dados', icon: '📈', path: '/relatorios', profiles: ['administrador', 'supervisor', 'financeiro'] },
    { title: 'Configurações', description: 'Sistema e usuários', icon: '⚙️', path: '/configuracoes', profiles: ['administrador'] }
  ];

  const filteredActions = quickActions.filter(a => a.profiles.includes(user?.perfil));

  const formatLabel = (label) => {
    if (!label) return 'Não definido';
    return String(label).charAt(0).toUpperCase() + String(label).slice(1);
  };

  const statsCards = dados ? [
    { title: 'Colaboradores', value: dados.colaboradores.total, sub: `${dados.colaboradores.ativos} ativos · ${dados.colaboradores.inativos} inativos`, icon: '👥', bg: '#4f46e5' },
    { title: 'Veículos', value: dados.veiculos.total, sub: `${dados.veiculos.ativos} ativos · ${dados.veiculos.manutencao} manutenção`, icon: '🚛', bg: '#0ea5e9' },
    { title: 'Pontos Hoje', value: dados.pontos.hoje, sub: `${dados.pontos.colaboradoresQueBateramHoje}/${dados.pontos.totalColaboradores} bateram ponto`, icon: '⏰', bg: '#f59e0b' },
    { title: 'Atestados Pendentes', value: dados.atestados.pendentes, sub: `${dados.atestados.total} no total`, icon: '📋', bg: '#ef4444' }
  ] : [];

  return (
    <div className="space-y-6 p-6 min-h-screen bg-neutral-light">
      {/* Header */}
      <div className="bg-white rounded-xl shadow-lg p-8">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="text-3xl md:text-4xl font-bold text-gray-900 mb-2">
              Bem-vindo, {user?.nome?.split(' ')[0] || 'Usuário'}! 👋
            </h1>
            <p className="text-gray-600">
              {new Date().toLocaleDateString('pt-BR', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}
            </p>
          </div>
          <div className="bg-indigo-50 px-5 py-3 rounded-xl text-right">
            <p className="text-sm text-indigo-600 font-medium">Perfil</p>
            <p className="text-xl font-bold text-indigo-900 capitalize">{user?.perfil || 'Não definido'}</p>
          </div>
        </div>
      </div>

      {loading ? (
        <div className="bg-white rounded-xl shadow p-12 text-center">
          <div className="animate-spin inline-block w-8 h-8 border-4 border-indigo-500 border-t-transparent rounded-full"></div>
          <p className="mt-3 text-gray-500">Carregando dados reais...</p>
        </div>
      ) : !dados ? (
        <div className="bg-white rounded-xl shadow p-12 text-center">
          <p className="text-gray-500">Não foi possível carregar os dados</p>
        </div>
      ) : (
        <>
          {/* Cards principais - dados reais */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {statsCards.map((stat, i) => (
              <div key={i} className="bg-white rounded-xl shadow p-5 flex items-center justify-between border-t-4 hover:shadow-lg transition" style={{ borderTopColor: stat.bg }}>
                <div>
                  <p className="text-sm font-medium text-gray-500">{stat.title}</p>
                  <p className="text-3xl font-bold text-gray-900">{stat.value}</p>
                  <p className="text-xs text-gray-400 mt-1">{stat.sub}</p>
                </div>
                <div className="w-14 h-14 rounded-xl flex items-center justify-center text-2xl" style={{ backgroundColor: stat.bg + '22' }}>
                  {stat.icon}
                </div>
              </div>
            ))}
          </div>

          {/* Segunda linha de cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <div className="bg-white rounded-xl shadow p-5 border-t-4 border-indigo-500">
              <p className="text-sm font-medium text-gray-500">🟢 Saldo Positivo (mês)</p>
              <p className="text-3xl font-bold text-green-600">{dados.folhaMes.saldoPositivo}</p>
              <p className="text-xs text-gray-400 mt-1">funcionários com horas positivas</p>
            </div>
            <div className="bg-white rounded-xl shadow p-5 border-t-4 border-red-500">
              <p className="text-sm font-medium text-gray-500">🔴 Saldo Negativo (mês)</p>
              <p className="text-3xl font-bold text-red-600">{dados.folhaMes.saldoNegativo}</p>
              <p className="text-xs text-gray-400 mt-1">funcionários com horas negativas</p>
            </div>
            <div className="bg-white rounded-xl shadow p-5 border-t-4 border-blue-500">
              <p className="text-sm font-medium text-gray-500">📅 Pontos no Mês</p>
              <p className="text-3xl font-bold text-blue-600">{dados.pontos.mes}</p>
              <p className="text-xs text-gray-400 mt-1">{dados.pontos.total} no total</p>
            </div>
            <div className="bg-white rounded-xl shadow p-5 border-t-4 border-gray-400">
              <p className="text-sm font-medium text-gray-500">🔧 Manutenções</p>
              <p className="text-3xl font-bold text-gray-700">{dados.manutencoes.total}</p>
              <p className="text-xs text-gray-400 mt-1">{dados.manutencoes.emAndamento} em andamento</p>
            </div>
          </div>

          {/* Análises */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            {/* Por perfil */}
            <div className="bg-white rounded-xl shadow p-6">
              <h3 className="text-lg font-bold text-gray-900 mb-4">👥 Colaboradores por Perfil</h3>
              <div className="space-y-3">
                {dados.colaboradores.porPerfil.map((p, i) => (
                  <div key={i}>
                    <div className="flex justify-between text-sm mb-1">
                      <span className="text-gray-600 capitalize">{formatLabel(p._id)}</span>
                      <span className="font-semibold text-gray-900">{p.total}</span>
                    </div>
                    <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                      <div
                        className="h-full bg-indigo-500 rounded-full"
                        style={{ width: `${(p.total / dados.colaboradores.total) * 100}%` }}
                      ></div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Por departamento */}
            <div className="bg-white rounded-xl shadow p-6">
              <h3 className="text-lg font-bold text-gray-900 mb-4">🏢 Por Departamento</h3>
              <div className="space-y-3">
                {dados.colaboradores.porDepartamento.map((p, i) => (
                  <div key={i}>
                    <div className="flex justify-between text-sm mb-1">
                      <span className="text-gray-600">{formatLabel(p._id)}</span>
                      <span className="font-semibold text-gray-900">{p.total}</span>
                    </div>
                    <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                      <div
                        className="h-full bg-emerald-500 rounded-full"
                        style={{ width: `${(p.total / dados.colaboradores.total) * 100}%` }}
                      ></div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Veículos por tipo */}
            <div className="bg-white rounded-xl shadow p-6">
              <h3 className="text-lg font-bold text-gray-900 mb-4">🚛 Veículos por Tipo</h3>
              <div className="space-y-3">
                {dados.veiculos.porTipo.map((p, i) => (
                  <div key={i}>
                    <div className="flex justify-between text-sm mb-1">
                      <span className="text-gray-600 uppercase">{formatLabel(p._id)}</span>
                      <span className="font-semibold text-gray-900">{p.total}</span>
                    </div>
                    <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                      <div
                        className="h-full bg-amber-500 rounded-full"
                        style={{ width: `${(p.total / dados.veiculos.total) * 100}%` }}
                      ></div>
                    </div>
                  </div>
                ))}
                {dados.veiculos.porTipo.length === 0 && (
                  <p className="text-gray-400 text-sm">Nenhum veículo cadastrado</p>
                )}
              </div>
            </div>
          </div>
        </>
      )}

      {/* Ações Rápidas */}
      <div className="bg-white rounded-xl shadow p-6">
        <h2 className="text-xl font-bold text-gray-900 mb-4">⚡ Ações Rápidas</h2>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
          {filteredActions.map((action, i) => (
            <Link key={i} to={action.path}
              className="group p-4 border-2 border-transparent rounded-xl hover:border-indigo-200 hover:bg-indigo-50/50 hover:shadow transition-all">
              <div className="flex items-center gap-3">
                <span className="text-2xl">{action.icon}</span>
                <div>
                  <h3 className="font-semibold text-gray-900 text-sm group-hover:text-indigo-700">{action.title}</h3>
                  <p className="text-xs text-gray-500">{action.description}</p>
                </div>
              </div>
            </Link>
          ))}
        </div>
      </div>

      {/* Status do sistema */}
      <div className="bg-white rounded-xl shadow p-6">
        <h2 className="text-lg font-bold text-gray-900 mb-4">🔧 Status do Sistema</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <div className="flex items-center justify-between p-3 bg-green-50 rounded-lg">
            <span className="text-gray-700 font-medium">Servidor</span>
            <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-green-100 text-green-800">
              <span className="w-2 h-2 bg-green-400 rounded-full mr-2"></span>Online
            </span>
          </div>
          <div className="flex items-center justify-between p-3 bg-green-50 rounded-lg">
            <span className="text-gray-700 font-medium">Database</span>
            <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-green-100 text-green-800">
              <span className="w-2 h-2 bg-green-400 rounded-full mr-2"></span>Conectado
            </span>
          </div>
          <div className="flex items-center justify-between p-3 bg-indigo-50 rounded-lg">
            <span className="text-gray-700 font-medium">Usuários ativos</span>
            <span className="text-sm font-semibold text-indigo-700">{dados?.usuarios?.ativos || 0}/{dados?.usuarios?.total || 0}</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
